import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

/**
 * SheetCountsProvider (ZY-10350)
 *
 * Shell-level registry of "how many open editor cards does each section
 * have right now?" Mounted at the patient shell so sibling consumers —
 * the sidebar badge, the beforeunload gate, the UnsavedCardsDialog — can
 * read a single source of truth without each section rolling its own
 * event bus.
 *
 * Writer side (editors):
 *   useSheetCountRegistration("chief-complaints", stack.sheets.length)
 * The hook pushes the count on every change and clears to 0 on unmount
 * (safe for the patient-leave path where the whole shell tears down;
 * irrelevant for the normal in-patient flow since Piece 2 keeps every
 * section's Editor mounted for the shell's lifetime).
 *
 * Reader side:
 *   useSheetCounts()      → shallow { sectionKey: number } snapshot
 *   useTotalOpenCards()   → sum across every section (beforeunload gate)
 *   useOpenSectionList()  → [sectionKey, count][] with count > 0 only
 *
 * All three re-render consumers only when the published map actually
 * changes value (setCount dedupes same-value writes).
 */

const SheetCountsContext = createContext(null);

export const SheetCountsProvider = ({ children }) => {
  const [counts, setCounts] = useState({});

  // Dedupe same-value writes so a sheet-stack `setState` that doesn't
  // change the length doesn't ripple through to every reader.
  const setCount = useCallback((sectionKey, count) => {
    if (!sectionKey) return;
    const n = Math.max(0, count | 0);
    setCounts((prev) => {
      const curr = prev[sectionKey] || 0;
      if (curr === n) return prev;
      const next = { ...prev };
      if (n === 0) {
        delete next[sectionKey];
      } else {
        next[sectionKey] = n;
      }
      return next;
    });
  }, []);

  const clearCount = useCallback((sectionKey) => {
    if (!sectionKey) return;
    setCounts((prev) => {
      if (!(sectionKey in prev)) return prev;
      const next = { ...prev };
      delete next[sectionKey];
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ counts, setCount, clearCount }),
    [counts, setCount, clearCount],
  );

  return (
    <SheetCountsContext.Provider value={value}>
      {children}
    </SheetCountsContext.Provider>
  );
};

SheetCountsProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * Editor-side hook — call once per editor with its current open-card
 * count. Changes are published on every re-render where `count` differs
 * from the last published value; unmount clears the section's entry
 * entirely (relevant only when the whole patient shell unmounts, since
 * ZY-10350's design keeps every editor mounted across section switches).
 *
 * A `null`/empty `sectionKey` is a silent no-op so a conditional caller
 * doesn't need to guard the hook.
 *
 * Safe to call outside the provider (returns a no-op) so unit tests and
 * early-mount scenarios don't need to wrap the whole tree.
 */
export const useSheetCountRegistration = (sectionKey, count) => {
  const ctx = useContext(SheetCountsContext);
  const setCount = ctx ? ctx.setCount : null;
  const clearCount = ctx ? ctx.clearCount : null;

  useEffect(() => {
    if (!setCount || !sectionKey) return undefined;
    setCount(sectionKey, count);
    return () => {
      if (clearCount) clearCount(sectionKey);
    };
  }, [sectionKey, count, setCount, clearCount]);
};

/** Shallow read of all section counts; drives the sidebar badge. */
export const useSheetCounts = () => {
  const ctx = useContext(SheetCountsContext);
  return ctx ? ctx.counts : EMPTY_COUNTS;
};

/** Derived total — the beforeunload gate reads this. */
export const useTotalOpenCards = () => {
  const counts = useSheetCounts();
  return useMemo(() => {
    let total = 0;
    for (const k of Object.keys(counts)) total += counts[k] || 0;
    return total;
  }, [counts]);
};

/**
 * Filtered + stable [sectionKey, count] pairs — fed to UnsavedCardsDialog
 * so the leave warning can list "Chief complaints · 2, Diagnosis · 1".
 * Order follows Object.entries, which in practice is insertion order; a
 * caller that needs a specific display order should sort by its own
 * section registry (SECTIONS in index.js).
 */
export const useOpenSectionList = () => {
  const counts = useSheetCounts();
  return useMemo(
    () => Object.entries(counts).filter(([, n]) => n > 0),
    [counts],
  );
};

// Reference-stable empty default so useSheetCounts() returning value from
// outside the provider doesn't churn consumers' memo deps.
const EMPTY_COUNTS = Object.freeze({});
